import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { HomeGuard } from 'app/core/auth/guards/home.guard';
import {AssociationPermissionsGuard} from 'app/core/auth/guards/association-permissions.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { NotFoundComponent } from '@portal/shared/not-found/not-found.component';
import {InitialDataResolver} from "./app.resolvers";
import {ProductCatalogComponent} from "./modules/admin/party/product-catalog/product-catalog.component";
import {QuoteRequestComponent} from "./modules/admin/party/product-catalog/quote-request/quote-request.component";
import { AppContextOrgTypeGuard } from 'app/core/auth/guards/app-context-org-type.guard';
import { AppContextPartiesGuard } from 'app/core/auth/guards/app-context-parties.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Public homepage: guests see the marketing homepage, authenticated users
    // are sent straight to '/parties' by the HomeGuard.
    {
        path: '',
        canActivate: [HomeGuard],
        canActivateChild: [HomeGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: '', loadChildren: () => import('app/modules/landing/home/home.module').then(m => m.LandingHomeModule) },
        ]
    },

    // Redirect signed in user to the '/example'
    //
    // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'parties' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.module').then(m => m.AuthConfirmationRequiredModule)
            },
            {
                path: 'confirm-email',
                loadChildren: () => import('app/modules/auth/confirm-email/confirm-email.module').then(m => m.AuthConfirmEmailModule)
            },
            {
                path: 'forgot-password',
                loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.module').then(m => m.AuthForgotPasswordModule)
            },
            {
                path: 'reset-password',
                loadChildren: () => import('app/modules/auth/reset-password/reset-password.module').then(m => m.AuthResetPasswordModule)
            },
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.module').then(m => m.AuthSignInModule) },
            { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.module').then(m => m.AuthSignUpModule) },
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.module').then(m => m.AuthSignOutModule) },
            {
                path: 'unlock-session',
                loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.module').then(m => m.AuthUnlockSessionModule)
            }
        ]
    },

    // Landing routes
    {

        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: '', loadChildren: () => import('app/modules/landing/landing.module').then(m => m.LandingModule) },
        ]
    },

    // Public routes (no authentication required)
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'public', loadChildren: () => import('app/modules/public/public.module').then(m => m.PublicModule) },
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        children: [
            {
                path: 'parties',
                canActivate: [AppContextPartiesGuard],
                loadChildren: () => import('app/modules/admin/user-parties/user-parties.module').then(m => m.UserPartiesModule)
            },
            {
                path: 'party',
                canActivate: [AppContextOrgTypeGuard],
                canActivateChild: [AppContextOrgTypeGuard, AssociationPermissionsGuard],
                loadChildren: () => import('app/modules/admin/party/party.module').then(m => m.PartyModule)
            },
            {
                path: 'org/:orgId/merchandising',
                pathMatch: 'full',
                redirectTo: 'org/:orgId/merchandising/overview'
            },
            {
                path: 'org/:orgId/inventory',
                canActivate: [AppContextOrgTypeGuard],
                canActivateChild: [AppContextOrgTypeGuard, AssociationPermissionsGuard],
                loadChildren: () => import('app/modules/admin/party/inventory/inventory.module').then(m => m.InventoryModule)
            },
            {
                path: 'org/:orgId/merchandising',
                canActivate: [AppContextOrgTypeGuard],
                canActivateChild: [AppContextOrgTypeGuard, AssociationPermissionsGuard],
                loadChildren: () => import('app/modules/admin/party/inventory/inventory.module').then(m => m.InventoryModule)
            },
            {
                path: 'org/:orgId/party-events/:partyEventId/inventory',
                redirectTo: 'party/events/:partyEventId/inventory'
            },
            {
                path: 'organizations',
                canActivate: [AppContextOrgTypeGuard],
                canActivateChild: [AppContextOrgTypeGuard, AssociationPermissionsGuard],
                loadChildren: () => import('app/modules/admin/organizations/organizations.module').then(m => m.OrganizationsModule)
            },
            {
                path: 'payments',
                loadChildren: () => import('app/modules/admin/payments/payments.module').then(m => m.PaymentsModule),
                resolve: {
                    initialData: InitialDataResolver
                },
            },
            {
                path: 'catalog',
                component: ProductCatalogComponent,
                data: {
                    authRedirect: '/product-catalog'
                }
            },
            {
                path: 'catalog/quote-request',
                component: QuoteRequestComponent,
                data: {
                    authRedirect: '/product-catalog/quote'
                }
            },
            {
                path: 'support', loadChildren: () => import('app/modules/admin/support/support.module').then(m => m.SupportModule),
                resolve: {
                    initialData: InitialDataResolver
                },
            },
            {
                path: 'admin',
                loadChildren: () => import('app/modules/management/management.module').then(m => m.ManagementModule)
            },
            {
                path: 'admin/project-tasks',
                loadChildren: () => import('app/modules/admin/project-tasks/project-tasks.module').then(m => m.ProjectTasksModule)
            },
            {
                path: 'timeline',
                loadChildren: () => import('app/modules/public/public.module').then(m => m.PublicModule)
            },
        ]
    },
    { path: '**', component: NotFoundComponent },
];
